import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ReportIdParamGuard from './components/ReportIdParamGuard';
import ReportsLayout from './components/ReportsLayout';
import ReportsUnauthorized from './components/ReportsUnauthorized';
import { useReportsPermissions } from './hooks/useReportsPermissions.hook';
import CustomersPage from './pages/CustomersPage';
import EditReportPage from './pages/EditReportPage';
import NewReportPage from './pages/NewReportPage';
import ReportBuilderPage from './pages/ReportBuilderPage';
import ReportPreviewPage from './pages/ReportPreviewPage';
import ReportPrintPage from './pages/ReportPrintPage';
import ReportViewPage from './pages/ReportViewPage';
import ReportsListPage from './pages/ReportsListPage';
import ReportsLoginPage from './pages/ReportsLoginPage';
import SectionLibraryPage from './pages/SectionLibraryPage';
import TemplatesPage from './pages/TemplatesPage';
import { LoadingPage } from '@act/storybook';
import {
  actingUserAppSelectors,
  verifyUser,
} from 'redux/slices/actingUserSlice.slice';
import { useAppDispatch } from 'redux/storeHooks';
import { HttpErrorStatus } from 'redux/utils/httpStatusCodes';

/**
 * Route table for the reports subdomain SPA.
 */
export const ReportsAppRouter = () => {
  const dispatch = useAppDispatch();
  const loadingStatus = actingUserAppSelectors.useSelectLoadingStatus();
  const authError = actingUserAppSelectors.useSelectActingUserError();
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const { canView, canManageCustomers } = useReportsPermissions();

  useEffect(() => {
    if (loadingStatus === 'initial') {
      dispatch(verifyUser({}));
    }
  }, [dispatch, loadingStatus]);

  if (loadingStatus === 'initial' || loadingStatus === 'loading') {
    return <LoadingPage />;
  }

  if (
    loadingStatus === 'failed' &&
    authError?.code === HttpErrorStatus.UNAUTHORIZED.toString()
  ) {
    return (
      <Routes>
        <Route path="login" element={<ReportsLoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (loadingStatus === 'failed') {
    return <ReportsUnauthorized />;
  }

  if (!actingUser) {
    return <LoadingPage />;
  }

  if (!canView) {
    return <ReportsUnauthorized />;
  }

  return (
    <Routes>
      <Route
        path=":reportId/print"
        element={
          <ReportIdParamGuard>
            <ReportPrintPage />
          </ReportIdParamGuard>
        }
      />
      <Route
        path=":reportId/view"
        element={
          <ReportIdParamGuard>
            <ReportViewPage />
          </ReportIdParamGuard>
        }
      />
      <Route element={<ReportsLayout />}>
        <Route index element={<ReportsListPage />} />
        <Route path="new" element={<NewReportPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="library" element={<SectionLibraryPage />} />
        <Route
          path="customers"
          element={
            canManageCustomers ? (
              <CustomersPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path=":reportId/edit"
          element={
            <ReportIdParamGuard>
              <EditReportPage />
            </ReportIdParamGuard>
          }
        />
        <Route
          path=":reportId"
          element={
            <ReportIdParamGuard>
              <ReportBuilderPage />
            </ReportIdParamGuard>
          }
        />
        <Route
          path=":reportId/preview"
          element={
            <ReportIdParamGuard>
              <ReportPreviewPage />
            </ReportIdParamGuard>
          }
        />
      </Route>
    </Routes>
  );
};
